import React, { useState, useMemo } from 'react';
import { Plus, Edit2, Trash2, X, Building2, Check, CalendarDays, Clock3 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { formatApiError } from '../utils/apiErrors';
import { formatUiDate as formatDate } from '../utils/uiFormat';
import { UiViewToggle } from '../components/ui/UiTabs';
import UiDateField from '../components/ui/UiDateField';
import UiTimeField from '../components/ui/UiTimeField';

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_ABBREVS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const hhmm = (t) => String(t || '').slice(0, 5); // strip seconds from a backend TimeField
const getLocalDate = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

const initialShopHours = { effective_from: getLocalDate(), open_time: '09:00', close_time: '18:00', notes: '', selectedDays: [] };
const initialHoliday = { name: '', start_date: getLocalDate(), end_date: '', notes: '' };

/**
 * ShopHoursTab — self-contained Shop Hours + Public Holidays management.
 * Option A: data (shopHours, publicHolidays) comes down as props; changes are
 * reported up via onDataChanged so the parent re-fetches and all tabs stay in sync.
 */
export default function ShopHoursTab({ shopHours = [], publicHolidays = [], hasPermission, adminUser, onDataChanged }) {
  const [listTab, setListTab] = useState('hours');           // hours | holidays
  const [modalTab, setModalTab] = useState('hours');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(initialShopHours);
  const [holidayForm, setHolidayForm] = useState(initialHoliday);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [pending, setPending] = useState([]);
  const [saving, setSaving] = useState(false);
  const [holidaySaving, setHolidaySaving] = useState(false);

  const canEdit = !hasPermission || adminUser;
  const refresh = () => onDataChanged && onDataChanged();

  // Group shop-hours rows by effective_from, then by open/close time
  const grouped = useMemo(() => {
    const byDate = {};
    shopHours.forEach((sh) => { (byDate[sh.effective_from] = byDate[sh.effective_from] || []).push(sh); });
    return Object.entries(byDate).sort(([a], [b]) => b.localeCompare(a)).map(([date, entries]) => {
      const byTimes = {};
      entries.forEach((e) => {
        const key = `${e.open_time}__${e.close_time}`;
        if (!byTimes[key]) byTimes[key] = { open_time: e.open_time, close_time: e.close_time, dayEntries: [], notes: e.notes };
        byTimes[key].dayEntries.push({ day: Number(e.day_of_week), id: e.id });
      });
      return {
        effective_from: date,
        groups: Object.values(byTimes).map((g) => ({
          ...g,
          dayEntries: [...g.dayEntries].sort((a, b) => a.day - b.day),
          days: [...g.dayEntries].map((x) => x.day).sort((a, b) => a - b),
          ids: [...g.dayEntries].map((x) => x.id),
        })),
      };
    });
  }, [shopHours]);

  // ── Shop hours: open modals ──
  const openAdd = () => {
    setEditingGroup(null); setEditingHoliday(null); setPending([]);
    setForm({ ...initialShopHours, effective_from: getLocalDate() });
    setModalTab('hours'); setShowModal(true);
  };
  const openEditGroup = (effectiveFrom, group) => {
    setEditingGroup({ ...group, effective_from: effectiveFrom });
    setEditingHoliday(null); setPending([]); setModalTab('hours');
    setForm({ effective_from: effectiveFrom, open_time: hhmm(group.open_time), close_time: hhmm(group.close_time), notes: group.notes || '', selectedDays: [...(group.days || [])] });
    setShowModal(true);
  };
  const openAddHoliday = () => {
    setEditingHoliday(null); setEditingGroup(null); setPending([]);
    setHolidayForm({ ...initialHoliday, start_date: getLocalDate() });
    setModalTab('holidays'); setShowModal(true);
  };
  const openEditHoliday = (h) => {
    setEditingHoliday(h); setEditingGroup(null); setModalTab('holidays');
    setHolidayForm({ name: h.name || '', start_date: h.start_date || getLocalDate(), end_date: h.end_date || '', notes: h.notes || '' });
    setShowModal(true);
  };
  const closeModal = () => {
    setShowModal(false); setEditingGroup(null); setEditingHoliday(null);
    setPending([]); setForm(initialShopHours); setHolidayForm(initialHoliday); setModalTab('hours');
  };

  // ── Shop hours: save ──
  // Apply expands the selected days into one pending row PER DAY (local only —
  // nothing hits the database until Save). Editing/removing is per-day.
  const applyPending = () => {
    const { effective_from, open_time, close_time, notes, selectedDays } = form;
    if (!effective_from || !open_time || !close_time) { toast.error('Fill date and times'); return; }
    if (selectedDays.length === 0) { toast.error('Select at least one day'); return; }
    setPending((prev) => {
      // Replace any existing pending row for the same day + effective date.
      const kept = prev.filter((r) => !(r.effective_from === effective_from && selectedDays.includes(r.day_of_week)));
      const rows = selectedDays.map((day) => ({
        _key: `${Date.now()}_${day}`, day_of_week: day, effective_from, open_time, close_time, notes,
      }));
      return [...kept, ...rows].sort((a, b) => a.effective_from.localeCompare(b.effective_from) || a.day_of_week - b.day_of_week);
    });
    setForm((f) => ({ ...f, selectedDays: [] }));
  };

  // Pull a pending row back into the form to adjust it, then re-Apply.
  const editPending = (row) => {
    setForm((f) => ({ ...f, effective_from: row.effective_from, open_time: hhmm(row.open_time), close_time: hhmm(row.close_time), notes: row.notes || '', selectedDays: [row.day_of_week] }));
    setPending((p) => p.filter((r) => r._key !== row._key));
  };

  // Save commits everything in a single atomic bulk request, then closes.
  const saveAll = async () => {
    let entries = [];
    let deleteIds = [];
    if (editingGroup) {
      const { effective_from, open_time, close_time, notes, selectedDays } = form;
      if (!effective_from || !open_time || !close_time) { toast.error('Fill date and times'); return; }
      entries = selectedDays.map((day) => ({ day_of_week: day, effective_from, open_time: hhmm(open_time), close_time: hhmm(close_time), notes: notes || '' }));
      deleteIds = (editingGroup.dayEntries || []).filter((e) => !selectedDays.includes(e.day)).map((e) => e.id);
      if (entries.length === 0 && deleteIds.length === 0) { toast.error('Select at least one day'); return; }
    } else {
      // Add mode — use pending rows, auto-applying the current form if days are still selected.
      const rows = [...pending];
      if (form.selectedDays.length > 0 && form.open_time && form.close_time && form.effective_from) {
        form.selectedDays.forEach((day) => rows.push({ day_of_week: day, effective_from: form.effective_from, open_time: form.open_time, close_time: form.close_time, notes: form.notes }));
      }
      entries = rows.map((r) => ({ day_of_week: r.day_of_week, effective_from: r.effective_from, open_time: hhmm(r.open_time), close_time: hhmm(r.close_time), notes: r.notes || '' }));
      if (entries.length === 0) { toast.error('Add at least one entry'); return; }
    }
    setSaving(true);
    try {
      await api.post('/shop-hours/bulk-save/', { entries, delete_ids: deleteIds });
      toast.success('Shop hours saved');
      closeModal(); refresh();
    } catch (e) { toast.error(formatApiError(e, 'Failed to save')); } finally { setSaving(false); }
  };

  const deleteGroup = async (ids, label) => {
    if (!window.confirm(`Delete shop hours for ${label}?`)) return;
    try {
      await Promise.all(ids.map((id) => api.delete(`/shop-hours/${id}/`)));
      toast.success('Deleted'); refresh();
    } catch (e) { toast.error(formatApiError(e, 'Failed to delete')); }
  };

  // ── Holidays: save/delete ──
  const saveHoliday = async () => {
    if (!holidayForm.name.trim() || !holidayForm.start_date) { toast.error('Provide a name and start date'); return; }
    setHolidaySaving(true);
    try {
      const payload = { name: holidayForm.name.trim(), start_date: holidayForm.start_date, end_date: holidayForm.end_date || null, notes: holidayForm.notes || '' };
      if (editingHoliday?.id) await api.patch(`/public-holidays/${editingHoliday.id}/`, payload);
      else await api.post('/public-holidays/', payload);
      toast.success(editingHoliday ? 'Holiday updated' : 'Holiday added');
      setHolidayForm(initialHoliday); setEditingHoliday(null); refresh();
    } catch (e) { toast.error(formatApiError(e, 'Failed to save holiday')); } finally { setHolidaySaving(false); }
  };
  const deleteHoliday = async (h) => {
    if (!window.confirm(`Delete public holiday "${h.name}"?`)) return;
    try { await api.delete(`/public-holidays/${h.id}/`); toast.success('Holiday deleted'); refresh(); }
    catch (e) { toast.error(formatApiError(e, 'Failed to delete holiday')); }
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm';

  return (
    <div className="space-y-4">
      {/* Sub-tab toggle + add buttons */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <UiViewToggle
          options={[{ value: 'hours', label: 'Shop Hours' }, { value: 'holidays', label: 'Public Holidays' }]}
          value={listTab}
          onChange={setListTab}
        />
        {canEdit && (listTab === 'hours'
          ? <button onClick={openAdd} className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"><Plus size={16} /> Set Shop Hours</button>
          : <button onClick={openAddHoliday} className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"><Plus size={16} /> Set Public Holiday</button>
        )}
      </div>

      {/* List */}
      {listTab === 'hours' ? (
        <div className="space-y-3">
          {grouped.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center text-gray-400 text-sm">No shop hours set.</div>
          ) : grouped.map(({ effective_from: effDate, groups }) => (
            <div key={effDate} className="bg-white rounded-xl border overflow-hidden">
              <div className="px-4 py-2 bg-gray-50 border-b text-xs font-semibold text-gray-500">Effective from {formatDate(effDate)}</div>
              {groups.map((g, gi) => (
                <div key={gi} className="flex items-center justify-between gap-3 px-4 py-3 border-b last:border-b-0">
                  <div className="flex flex-wrap gap-1">
                    {g.days.map((d) => <span key={d} className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded text-xs font-semibold">{DAY_ABBREVS[d]}</span>)}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <span className="font-mono">{hhmm(g.open_time)} - {hhmm(g.close_time)}</span>
                    {g.notes && <span className="text-gray-400 text-xs">{g.notes}</span>}
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEditGroup(effDate, g)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Edit2 size={14} /></button>
                      <button onClick={() => deleteGroup(g.ids, `${g.days.map((d) => DAY_ABBREVS[d]).join(', ')} (${effDate})`)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {publicHolidays.length === 0 ? (
            <div className="bg-white rounded-xl border p-8 text-center text-gray-400 text-sm">No public holidays defined.</div>
          ) : publicHolidays.map((h) => (
            <div key={h.id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-amber-200 bg-amber-50">
              <div className="min-w-0">
                <div className="font-semibold text-amber-900 text-sm">{h.name}</div>
                <div className="text-xs text-amber-700">{h.end_date && h.end_date !== h.start_date ? `${formatDate(h.start_date)} to ${formatDate(h.end_date)}` : formatDate(h.start_date)}</div>
                {h.notes && <div className="text-xs text-amber-600 mt-1">{h.notes}</div>}
              </div>
              {canEdit && (
                <div className="flex items-center gap-1">
                  <button onClick={() => openEditHoliday(h)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"><Edit2 size={14} /></button>
                  <button onClick={() => deleteHoliday(h)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 size={14} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><Building2 size={18} /> {modalTab === 'holidays' ? (editingHoliday ? 'Edit Public Holiday' : 'Set Public Holiday') : (editingGroup ? 'Edit Shop Hours' : 'Set Shop Hours')}</h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-4">
              {modalTab === 'holidays' ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><label className="block text-xs text-gray-500 mb-1">Start Date *</label><UiDateField value={holidayForm.start_date} onChange={(v) => setHolidayForm((f) => ({ ...f, start_date: v }))} /></div>
                    <div><label className="block text-xs text-gray-500 mb-1">End Date</label><UiDateField value={holidayForm.end_date} onChange={(v) => setHolidayForm((f) => ({ ...f, end_date: v }))} /></div>
                    <div><label className="block text-xs text-gray-500 mb-1">Holiday Name *</label><input value={holidayForm.name} onChange={(e) => setHolidayForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} placeholder="e.g. Eid Holiday" /></div>
                    <div><label className="block text-xs text-gray-500 mb-1">Notes</label><input value={holidayForm.notes} onChange={(e) => setHolidayForm((f) => ({ ...f, notes: e.target.value }))} className={inputCls} placeholder="Optional" /></div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2 border-t">
                    <button onClick={closeModal} className="px-4 py-2 border border-gray-200 rounded-lg text-sm">Cancel</button>
                    <button onClick={saveHoliday} disabled={holidaySaving} className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">{editingHoliday ? 'Update Holiday' : 'Save Holiday'}</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div><label className="block text-xs text-gray-500 mb-1">Effective From *</label><UiDateField value={form.effective_from} onChange={(v) => setForm((f) => ({ ...f, effective_from: v }))} /></div>
                    <div><label className="block text-xs text-gray-500 mb-1">Opening Time *</label><UiTimeField value={form.open_time} onChange={(v) => setForm((f) => ({ ...f, open_time: v }))} /></div>
                    <div><label className="block text-xs text-gray-500 mb-1">Closing Time *</label><UiTimeField value={form.close_time} onChange={(v) => setForm((f) => ({ ...f, close_time: v }))} /></div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Apply to Days *</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {DAY_NAMES.map((name, i) => {
                        const checked = form.selectedDays.includes(i);
                        return <button key={i} type="button" onClick={() => setForm((f) => ({ ...f, selectedDays: checked ? f.selectedDays.filter((d) => d !== i) : [...f.selectedDays, i] }))} className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${checked ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300'}`}>{DAY_ABBREVS[i]}</button>;
                      })}
                      <button type="button" onClick={() => setForm((f) => ({ ...f, selectedDays: f.selectedDays.length === 7 ? [] : [0, 1, 2, 3, 4, 5, 6] }))} className="px-3 py-1.5 text-xs text-gray-500 border border-dashed border-gray-300 rounded-lg">{form.selectedDays.length === 7 ? 'Deselect All' : 'Select All'}</button>
                    </div>
                  </div>
                  <div className="flex items-end gap-3">
                    <div className="flex-1"><label className="block text-xs text-gray-500 mb-1">Notes</label><input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="e.g. Ramadan hours" className={inputCls} /></div>
                    {!editingGroup && <button type="button" onClick={applyPending} className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"><Check size={14} /> Apply</button>}
                  </div>
                  {!editingGroup && pending.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Pending — {pending.length} row{pending.length !== 1 ? 's' : ''} (one per day, saved together)</p>
                      <div className="border rounded-lg divide-y overflow-hidden">
                        {pending.map((row) => (
                          <div key={row._key} className="flex items-center justify-between px-3 py-2 text-sm">
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded text-xs font-semibold w-9 text-center">{DAY_ABBREVS[row.day_of_week]}</span>
                              <span className="text-gray-700 font-medium">{formatDate(row.effective_from)}</span>
                              <span className="font-mono text-gray-600 text-xs">{hhmm(row.open_time)} – {hhmm(row.close_time)}</span>
                              {row.notes && <span className="text-xs text-gray-400 italic">{row.notes}</span>}
                            </div>
                            <div className="flex items-center gap-1">
                              <button onClick={() => editPending(row)} title="Edit" className="p-1 text-blue-500 hover:text-blue-700 rounded"><Edit2 size={14} /></button>
                              <button onClick={() => setPending((p) => p.filter((r) => r._key !== row._key))} title="Delete" className="p-1 text-gray-400 hover:text-red-600 rounded"><Trash2 size={14} /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end gap-2 pt-2 border-t">
                    <button onClick={closeModal} className="px-4 py-2 border border-gray-200 rounded-lg text-sm">Cancel</button>
                    <button onClick={saveAll} disabled={saving} className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">{saving ? 'Saving…' : (editingGroup ? 'Update' : 'Save')}</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
